/*
 * Elementy zaufania wspólne dla strony głównej, landingów Ads i prerenderu
 * (scripts/prerender-landings.mjs): portrety prawników i opinie z Google.
 * Plik .mjs bez Reacta — te same funkcje budują HTML w przeglądarce i przy
 * budowaniu strony statycznej. Teksty z CMS-a zawsze przez esc().
 *
 * Zdjęcia: prawdziwe portrety z kancelaria-olc.pl (kadr 4:5, jednolite ciepłe tło,
 * lekka korekta kolorów; wycięcie tła i kadrowanie — bez retuszu twarzy), pliki
 * w public/zespol/<slug>-{160,240,320}.webp. Źródła mają 400 px, więc nie ma
 * większych wariantów (powiększanie pogorszyłoby jakość).
 */

import { esc } from "./landingCopy.mjs";

/* Tytuły i izby dokładnie jak na kancelaria-olc.pl/zespol (nazwa → dane statyczne).
   Dane zmienne (numer wpisu, specjalizacje, opis) idą z CMS-a (/admin/zespol).
   Izbę podajemy tylko tam, gdzie wynika z opisu osoby; reszta — pole "Izba" w panelu zespołu. */
export const PEOPLE = {
  "Paweł Artymionek": { slug: "pawel-artymionek", title: "Radca prawny", izba: "", hero: true },
  "Adam Citko": { slug: "adam-citko", title: "Radca prawny", izba: "", hero: true },
  "Mateusz Siejko": { slug: "mateusz-siejko", title: "Radca prawny", izba: "Okręgowa Izba Radców Prawnych we Wrocławiu" },
  "Anna Samsik": { slug: "anna-samsik", title: "Radca prawna", izba: "" },
  "Olga Chowańska-Prus": { slug: "olga-chowanska-prus", title: "Adwokat", izba: "Wrocławska Izba Adwokacka" },
  "Katarzyna Nowak": { slug: "katarzyna-nowak", title: "Adwokat", izba: "" },
  "Sabina Moruś-Budny": { slug: "sabina-morus-budny", title: "Adwokat", izba: "" },
};
const ORDER = Object.keys(PEOPLE);

/* Bez „lookbehind” (?<=…): Safari starsze niż 16.4 nie parsuje takiego wyrażenia i cała paczka aplikacji się wtedy nie ładuje.
   Granica zdania: znak kończący zdanie + spacje + wielka litera; znacznik \u0001 wstawiamy za znakiem kończącym i po nim dzielimy. */
const SENT_MARK = /([.!?])\s+(?=[A-ZĄĆĘŁŃÓŚŹŻ])/g;

/** Najwyżej dwa pierwsze zdania opisu (łącznie do ok. 300 znaków, zawsze co najmniej pierwsze). */
export function shortBio(bio) {
  const flat = String(bio || "").replace(/\s+/g, " ").trim();
  if (!flat) return "";
  const out = [];
  for (const s of flat.replace(SENT_MARK, "$1\u0001").split("\u0001")) {
    if (out.length >= 2 || (out.length && out.join(" ").length + s.length > 300)) break;
    out.push(s);
  }
  return out.join(" ");
}

/** Zespół z CMS-a + dane statyczne: kolejność jak w PEOPLE, potem pozostali. Zwraca tylko osoby z nazwiskiem. */
export function resolvePeople(team) {
  const list = (Array.isArray(team) ? team : []).filter((p) => p && p.name);
  const people = list.map((p) => {
    const st = PEOPLE[p.name] || {};
    const [first, ...rest] = String(p.role || "").split("·").map((x) => x.trim()).filter(Boolean);
    return {
      name: p.name,
      title: st.title || first || "",
      specs: rest,
      bar: p.bar || "",
      izba: p.chamber || st.izba || "",
      bio: shortBio(p.bio),
      slug: st.slug || "",
      hero: !!st.hero,
      initials: p.initials || p.name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase(),
    };
  });
  const idx = (n) => (ORDER.includes(n) ? ORDER.indexOf(n) : 99);
  return people.sort((a, b) => idx(a.name) - idx(b.name));
}

const altOf = (p) => `${p.name}, ${String(p.title).toLowerCase()}`;

/** <img> portretu z srcset i jawnymi wymiarami; priority = eager + fetchpriority high (tylko hero). */
export function portraitImg(p, { sizes = "160px", priority = false, eager = false, prefix = "" } = {}) {
  if (!p.slug) return `<span class="pp-ini" aria-hidden="true">${esc(p.initials)}</span>`;
  const f = (w) => `/zespol/${prefix}${p.slug}-${w}.webp`;
  return (
    `<img src="${f(240)}" srcset="${f(160)} 160w, ${f(240)} 240w, ${f(320)} 320w" sizes="${sizes}" width="320" height="400" ` +
    `alt="${esc(altOf(p))}" ${priority ? 'loading="eager" fetchpriority="high"' : eager ? 'loading="eager"' : 'loading="lazy"'} decoding="async" />`
  );
}

/** Karta osoby: zdjęcie, imię i nazwisko, tytuł (+ numer wpisu, izba), opcjonalnie specjalizacje i opis. */
export function personCardHtml(p, { bio = true, sizes = "(max-width: 640px) 45vw, 240px" } = {}) {
  const t = [p.title, p.bar ? `nr wpisu ${p.bar}` : ""].filter(Boolean).join(" · ");
  return (
    `<article class="pp-card"><div class="pp-ph">${portraitImg(p, { sizes })}</div><div class="pp-t"><b>${esc(p.name)}</b>` +
    `<span class="pp-title">${esc(t)}</span>` +
    (p.izba ? `<span class="pp-izba">${esc(p.izba)}</span>` : "") +
    (bio && p.specs.length ? `<span class="pp-spec">${esc(p.specs.join(" · "))}</span>` : "") +
    (bio && p.bio ? `<p>${esc(p.bio)}</p>` : "") +
    `</div></article>`
  );
}

/** Statyczny rząd osób (prerender; w React zastępuje go suwak). */
export function peopleRowHtml(people, n = 3) {
  return `<div class="pp-row">${people.slice(0, n).map((p) => personCardHtml(p, { bio: false })).join("")}</div>`;
}

/* Półprzezroczysta Temida — zdjęcie posągu z wyciętym tłem, tonem mosiądzu i zanikiem u dołu
   (public/zespol/temida*.webp, proporcje 900×1062, z kanałem alfa). Dekoracja za zdjęciem mecenasów:
   puste alt, aria-hidden; krycie i położenie z CSS (.hd-themis / .lp-duo-themis).
   To największy obraz pierwszego ekranu, a więc element LCP — dlatego ładuje się od razu (nie leniwie),
   w małych wariantach (srcset), z wysokim priorytetem, żeby nie opóźniać LCP. */
const themis = (cls) =>
  `<img class="${cls}" src="/zespol/temida-540.webp" srcset="/zespol/temida-320.webp 320w, /zespol/temida-540.webp 540w, /zespol/temida.webp 900w" sizes="(max-width: 899px) 270px, 270px" width="900" height="1062" alt="" aria-hidden="true" fetchpriority="high" decoding="async" />`;

/** Hero strony głównej: dwóch mecenasów na bordowym kształcie + tabliczka. Pusty, gdy brakuje zdjęć. */
export function heroDuoHtml(people) {
  const duo = ["Paweł Artymionek", "Adam Citko"].map((n) => people.find((p) => p.name === n)).filter((p) => p && p.slug);
  if (duo.length < 2) return "";
  const img = (p, i) =>
    `<div class="hd-p hd-p${i}">${portraitImg(p, { sizes: "(max-width: 640px) 44vw, 280px", priority: true, prefix: "hero-" })}</div>`;
  const label = (p) =>
    `<li><b>${esc(p.name)}</b><span>${esc([p.title, p.bar ? `nr wpisu ${p.bar}` : "", p.izba].filter(Boolean).join(" · "))}</span></li>`;
  return (
    `<figure class="hero-duo"><div class="hd-stage">${themis("hd-themis")}${duo.map(img).join("")}</div>` +
    `<figcaption><ul>${duo.map(label).join("")}</ul><p>Każdą opinię podpisuje radca prawny.</p></figcaption></figure>`
  );
}

/** Landing, lewa kolumna hero (tylko szeroki ekran): kompaktowy beżowy panel z dwoma mecenasami. */
export function lpDuoHtml(people) {
  const duo = ["Paweł Artymionek", "Adam Citko"].map((n) => people.find((p) => p.name === n)).filter((p) => p && p.slug);
  if (duo.length < 2) return "";
  const li = (p) =>
    `<li><b>${esc(p.name)}</b><span>${esc([p.title, p.bar ? `nr wpisu ${p.bar}` : ""].filter(Boolean).join(" · "))}</span></li>`;
  /* Jeden gotowy obraz (Temida w tle + dwaj mecenasi, cień i zanik u dołu wypalone w pliku) zamiast trzech nałożonych
     <img> z filtrami CSS: mniej żądań i mniej malowania na telefonie, a to element LCP. Generator: scripts/make-hero-duo.py. */
  const alt = `${duo.map((p) => p.name).join(" i ")}, radcowie prawni`;
  return (
    `<div class="lp-duo"><div class="lp-duo-ph"><picture>` +
    `<source media="(min-width: 900px)" srcset="/zespol/lp-duo-d-1x.webp 1x, /zespol/lp-duo-d-2x.webp 2x" width="260" height="230">` +
    `<img class="lp-duo-img" src="/zespol/lp-duo-m-1x.webp" srcset="/zespol/lp-duo-m-1x.webp 1x, /zespol/lp-duo-m-2x.webp 2x" width="330" height="276" alt="${esc(alt)}" fetchpriority="high" decoding="async" />` +
    `</picture></div><ul class="lp-duo-t">${duo.map(li).join("")}</ul></div>`
  );
}

/** Podpis przy przycisku zamówienia: nazwana osoba z konfiguracji albo trzy miniatury zespołu. */
export function signRowHtml(people, signName) {
  const named = signName ? people.find((p) => p.name === signName) : null;
  if (named) {
    const short = `${String(named.title).toLowerCase().startsWith("adwokat") ? "adw." : "r. pr."} ${named.name}`;
    return (
      `<div class="pp-sign"><span class="pp-thumb">${portraitImg(named, { sizes: "44px" })}</span>` +
      `<span class="pp-sign-t">Podpisuje: <b>${esc(short)}</b></span></div>`
    );
  }
  const three = people.filter((p) => p.slug).slice(0, 3);
  if (!three.length) return "";
  return (
    `<div class="pp-sign"><span class="pp-thumbs">${three.map((p) => `<span class="pp-thumb">${portraitImg(p, { sizes: "44px" })}</span>`).join("")}</span>` +
    `<span class="pp-sign-t">Opinię przygotowuje i podpisuje radca prawny lub adwokat z zespołu — imieniem, nazwiskiem i numerem wpisu.</span></div>`
  );
}

export const REVIEWS_TITLE = "Opinie o OLC Kancelarii Radców Prawnych, której prawnicy przygotowują opinie w Prawly";

const num = (v) => String(v ?? "").trim();

/** Opinie z wizytówki Google kancelarii OLC (wpisane w panelu). Pusty, gdy brak pozycji. */
const shuffled = (a) => {
  const r = a.slice();
  for (let i = r.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [r[i], r[j]] = [r[j], r[i]];
  }
  return r;
};

/* random: losowa próbka z całej puli przy każdym wywołaniu (wołać w useMemo, żeby nie zmieniała się
   przy każdym renderze — zmienia się dopiero po przeładowaniu strony). Prerender bierze pierwsze N. */
/* Opinie dopasowane do tematu strony: gdy podano `topic` (RegExp), opinie pasujące treścią idą pierwsze,
   a opinie o kredytach frankowych — na końcu (większość profilu to frankowicze, a strona dotyczy czegoś
   innego). Treści opinii nie zmieniamy, tylko wybieramy i układamy kolejność; bez `topic` zachowanie jak dotąd. */
const FRANK_RE = /frank|chf|kredyt/iu;

export function reviewsHtml(rv, { max = 5, link = true, random = false, topic, avoid } = {}) {
  const all = (Array.isArray(rv?.items) ? rv.items : []).filter((x) => x && num(x.text));
  const base = random ? shuffled(all) : all;
  const ordered = topic
    ? base
        .map((x) => ({ x, s: (topic.test(num(x.text)) ? 2 : 0) - (FRANK_RE.test(num(x.text)) ? 1 : 0) - (avoid && avoid.test(num(x.text)) ? 5 : 0) }))
        .sort((a, b) => b.s - a.s)
        .map((o) => o.x)
    : base;
  const items = ordered.slice(0, max);
  if (!items.length) return "";
  const rating = num(rv.rating);
  const ratingNum = parseFloat(rating.replace(",", "."));
  const meta = [
    `Źródło: ${esc(rv.source || "Google")}`,
    rating ? `średnia ${esc(rating)} / 5` : "",
    num(rv.count) ? `${esc(num(rv.count))} opinii` : "",
  ].filter(Boolean);
  const more = link && num(rv.url) ? `<p class="rv-more"><a class="btn" href="${esc(num(rv.url))}" target="_blank" rel="noopener noreferrer">Zobacz wszystkie opinie w Google</a></p>` : "";
  // plakietka z oceną zbiorczą: gwiazdki wypełnione proporcjonalnie do średniej (nie do pojedynczych opinii)
  const badge = rating && Number.isFinite(ratingNum)
    ? `<div class="rv-badge"><span class="rv-g">${esc(rv.source || "Google")}</span><b>${esc(rating)}</b>` +
      `<span class="rv-stars-agg" role="img" aria-label="Średnia ocena ${esc(rating)} na 5" style="--r:${Math.max(0, Math.min(100, (ratingNum / 5) * 100)).toFixed(0)}%">★★★★★</span>` +
      `${num(rv.count) ? `<small>${esc(num(rv.count))} opinii</small>` : ""}</div>`
    : "";
  const stars = (n) => {
    const k = Math.max(0, Math.min(5, Math.round(Number(n) || 0)));
    return k ? `<span class="rv-stars" role="img" aria-label="${k} z 5">${"★".repeat(k)}${"☆".repeat(5 - k)}</span>` : "";
  };
  const initial = (a) => esc(String(a || "?").trim().charAt(0).toUpperCase() || "?");
  return (
    `<div class="rv"><div class="rv-head"><div><h2>${esc(rv.title || REVIEWS_TITLE)}</h2>` +
    `<p class="rv-meta">${meta.join(" · ")}</p></div>${badge}</div>` +
    `<div class="rv-list rv-n${items.length}">${items
      .map(
        (x) =>
          `<figure class="rv-card">${stars(x.stars)}<blockquote>${esc(x.text)}</blockquote>` +
          `<figcaption><span class="rv-av" aria-hidden="true">${initial(x.author)}</span><span>${esc(num(x.author))}${num(x.when) ? ` · ${esc(num(x.when))}` : ""}</span></figcaption></figure>`
      )
      .join("")}</div>${more}</div>`
  );
}
